import { useQuery } from '@tanstack/react-query';
import { Button, toast } from '@mlm/ui';
import {
  certificateShareText,
  certificateVerifyUrl,
  facebookShareUrl,
  linkedInAddToProfileUrl,
  linkedInShareUrl,
} from '@mlm/shared';
import { getTenantConfig } from '../lib/api';

/**
 * Share an issued certificate.
 *
 * Everything points at `/verify-certificate/<code>`, the page that already
 * exists and is already public — a share link reveals nothing that typing the
 * code would not. Until now a PNG download was the only output, which meant the
 * credential could be shown but never linked to.
 */
export function CertificateShare({
  code,
  courseTitle,
  issuedAt,
}: {
  code: string;
  courseTitle: string;
  issuedAt: string;
}) {
  const tenant = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  const org = tenant.data?.name ?? 'our academy';
  const url = certificateVerifyUrl(window.location.origin, code);
  const text = certificateShareText({ courseTitle, organizationName: org });
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Verification link copied.');
    } catch {
      toast.error('Could not copy the link. Long-press it to copy manually.');
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Share your certificate. Anyone with the link can verify it against your code.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={copy}>
          Copy link
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            // Opened synchronously off the click so a popup blocker does not
            // eat it; every URL here is already computed.
            window.open(
              linkedInAddToProfileUrl({
                name: courseTitle,
                organizationName: org,
                issuedAt,
                certUrl: url,
                certId: code,
              }),
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          Add to LinkedIn profile
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => window.open(linkedInShareUrl(url), '_blank', 'noopener,noreferrer')}
        >
          Share on LinkedIn
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => window.open(facebookShareUrl(url), '_blank', 'noopener,noreferrer')}
        >
          Share on Facebook
        </Button>
        {canNativeShare && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              // The share sheet is the natural gesture on a phone; a rejected
              // or dismissed sheet is not an error worth reporting.
              void navigator.share({ title: courseTitle, text, url }).catch(() => {});
            }}
          >
            Share…
          </Button>
        )}
      </div>
    </div>
  );
}
